import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaGraduationCap,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaExclamationTriangle,
  FaChartLine,
  FaCalendarAlt,
  FaBuilding,
  FaUserCheck,
  FaIdCard,
  FaFilter,
  FaTimes,
  FaTrophy,
  FaCut,
  FaUsers,
  FaChartBar,
} from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import apiRequest from "../services/api";
import ExamAnalysisModal from "../components/ExamAnalysisModal";
import logo from "../images/moh-logo.png";

const ResultPublic = () => {
  const [results, setResults] = useState([]);
  const [resultId, setResultId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [fanInput, setFanInput] = useState("");
  const [activeFan, setActiveFan] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchResults = useCallback(async (targetFan) => {
    if (!targetFan || !targetFan.trim()) {
      setResults([]);
      setError("Please enter a valid FAN identifier to verify.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await apiRequest(
        `users/results/public?query=${encodeURIComponent(targetFan.trim())}`,
        { method: "GET" }
      );

      if (data?.success && Array.isArray(data.data)) {
        setResults(data.data);
        setActiveFan(targetFan.trim());
      } else {
        setResults([]);
        setError(
          data?.message ||
            `No examination records found for query: "${targetFan}"`
        );
      }
    } catch (err) {
      console.error("Verification Fetch Error:", err);
      setResults([]);
      setError(
        err.message || "An unexpected error occurred while fetching records."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userString = localStorage.getItem("user");
    const storedUser = userString ? JSON.parse(userString) : null;
    const initialFan = storedUser?.username || "";

    setFanInput(initialFan);
    if (initialFan) {
      fetchResults(initialFan);
    }
  }, [fetchResults]);

  const handleFanSearchSubmit = (e) => {
    e.preventDefault();
    fetchResults(fanInput);
  };

  const handleClearFan = () => {
    setFanInput("");
    setActiveFan("");
    setResults([]);
    setError("");
  };

  const filteredResults = results.filter((r) => {
    const term = searchTerm.toLowerCase();
    return (
      r.user?.name?.toLowerCase().includes(term) ||
      r.center?.name?.toLowerCase().includes(term) ||
      r.round?.name?.toLowerCase().includes(term) ||
      r.date?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 p-4 sm:p-8 relative">
      <div className="max-w-7xl mx-auto flex flex-col gap-6 relative z-10">
        <div className="flex flex-col gap-6 bg-white border border-slate-200 p-6 rounded-3xl shadow-sm">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="relative flex items-center justify-center p-2 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm h-16 w-16 flex-shrink-0 overflow-hidden">
                <img
                  src={logo}
                  alt="Organization Logo"
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="hidden flex-col items-center justify-center text-sky-600"
                  style={{ display: "none" }}
                >
                  <FaGraduationCap className="text-2xl" />
                </div>
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-wide">
                  Ethiopian Health Professionals Licensing Examination
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  Result Verification Portal
                </p>
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          <form
            onSubmit={handleFanSearchSubmit}
            className="flex flex-col sm:flex-row items-center gap-3"
          >
            <div className="relative flex-1 w-full">
              <FaIdCard className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sky-600 text-sm" />
              <input
                type="text"
                placeholder="Enter Candidate IDNO or Mobile or FAN"
                value={fanInput}
                onChange={(e) => setFanInput(e.target.value)}
                className="w-full bg-slate-50 border border-slate-300 rounded-2xl pl-10 pr-10 py-3 text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 focus:bg-white transition font-mono"
              />
              {fanInput && (
                <button
                  type="button"
                  onClick={handleClearFan}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !fanInput.trim()}
              className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-xs tracking-wider uppercase transition shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 flex-shrink-0"
            >
              {loading ? (
                <FaSpinner className="animate-spin text-sm" />
              ) : (
                <FaSearch className="text-xs" />
              )}
              <span>Search</span>
            </button>
          </form>

          {activeFan && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Currently showing records for:</span>
              <span className="bg-sky-50 border border-sky-200 font-mono px-2.5 py-0.5 rounded-lg text-sky-700 font-bold">
                {activeFan}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-2xl text-xs font-medium">
            <FaExclamationTriangle className="text-lg flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-600 font-bold">
                  <th className="px-6 py-4">
                    <span className="flex items-center gap-2">
                      <FaCalendarAlt className="text-sky-600" /> Date
                    </span>
                  </th>
                  <th className="px-6 py-4">
                    <span className="flex items-center gap-2">
                      <FaUserCheck className="text-sky-600" /> Candidate
                    </span>
                  </th>
                  <th className="px-6 py-4">
                    <span className="flex items-center gap-2">
                      <FaBuilding className="text-sky-600" /> Exam Center
                    </span>
                  </th>
                  <th className="px-6 py-4">Exam Round</th>
                  <th className="px-6 py-4 text-center">Total Score</th>
                  <th className="px-6 py-4 text-center">Pass Mark</th>
                  <th className="px-6 py-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-16 text-center text-sky-600">
                      <div className="flex flex-col items-center gap-3">
                        <FaSpinner className="animate-spin text-3xl" />
                        <span className="text-xs text-slate-500 font-medium">
                          Querying records ...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredResults.length > 0 ? (
                  filteredResults.map((r) => {
                    const isPassed = r.decision?.toUpperCase() === "PASS";
                    return (
                      <tr
                        key={r.id}
                        onClick={() => {
                          setResultId(r.id);
                          setIsModalOpen(true);
                        }}
                        className="hover:bg-slate-50 transition cursor-pointer group"
                      >
                        <td className="px-6 py-4 font-mono text-slate-500 whitespace-nowrap">
                          {r.date || "N/A"}
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-900 capitalize">
                          {r.user?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {r.center?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-slate-500">
                          {r.round?.name || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-center font-mono font-bold text-sky-700">
                          {r.mark !== undefined
                            ? Number(r.mark).toFixed(1)
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 text-center font-mono text-slate-500">
                          {r.cut !== undefined
                            ? Number(r.cut).toFixed(1)
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${
                              isPassed
                                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                : "bg-rose-50 border-rose-200 text-rose-700"
                            }`}
                          >
                            {isPassed ? <FaCheckCircle /> : <FaTimesCircle />}
                            {r.decision || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 border border-sky-200 text-sky-700 text-[11px] font-semibold transition">
                            <FaChartLine /> Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      className="py-12 text-center text-slate-400 text-xs"
                    >
                      {activeFan
                        ? "No examination records found for this query."
                        : "Enter a Candidate IDNO or Mobile or FAN above to perform verification."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ExamAnalysisModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        resultId={resultId}
      />
    </div>
  );
};

export default ResultPublic;
